import React from "react";
import { ChevronDown, ChevronUp, FileText, PenLine, Sparkles } from "lucide-react";
import ContentBriefPanel from "./ContentBriefPanel";
import ExpandableText from "./ExpandableText";
import { ProviderIcon } from "../../shared/ProviderIcon";
import { PROVIDER_ORDER, toPayloadProvider } from "./providerMap";
import { TIER_TEXT_CLASS, tierOf } from "./tierColors";

const PRIORITY_DOT_CLASS = {
	critical: "monitoring__analysis-strategy-priority-dot--critical",
	high: "monitoring__analysis-strategy-priority-dot--high",
	medium: "monitoring__analysis-strategy-priority-dot--medium",
	low: "monitoring__analysis-strategy-priority-dot--low",
};

const PRIORITY_TEXT_CLASS = {
	critical: "monitoring__analysis-strategy-priority-text--critical",
	high: "monitoring__analysis-strategy-priority-text--high",
	medium: "monitoring__analysis-strategy-priority-text--medium",
	low: "monitoring__analysis-strategy-priority-text--low",
};

export default function CreateContentRow({
	rec,
	crossProviderSignals,
	briefOpen,
	onToggleBrief,
	writingRunId,
	onUseWritingAssistant,
	onViewContent,
}) {
	const hasBrief =
		(rec.content_requirements?.length ?? 0) > 0 ||
		(rec.facts_to_verify?.length ?? 0) > 0;

	const priority = rec.priority ?? "low";
	const coverage = rec.provider_impact?.coverage ?? 0;
	const tier = tierOf(coverage);

	return (
		<div className="monitoring__analysis-strategy-rec-row monitoring__analysis-strategy-rec-row--create">
			<div className="monitoring__analysis-strategy-rec-row-create-aside">
				<span className="monitoring__analysis-strategy-priority-tag">
					<span
						className={`monitoring__analysis-strategy-priority-dot ${
							PRIORITY_DOT_CLASS[priority] ?? PRIORITY_DOT_CLASS.low
						}`}
						aria-hidden
					/>
					<span className={PRIORITY_TEXT_CLASS[priority] ?? PRIORITY_TEXT_CLASS.low}>
						{priority}
					</span>
				</span>
				<div className="monitoring__analysis-strategy-rec-row-create-icon" aria-hidden>
					<FileText size={18} />
				</div>
			</div>

			<div className="monitoring__analysis-strategy-rec-row-create-main">
				<div className="monitoring__analysis-strategy-rec-row-create-title-row">
					<h6 className="monitoring__analysis-strategy-rec-row-create-title mb-0">
						{rec.title}
					</h6>
					{rec.target.content_type && (
						<span className="monitoring__analysis-strategy-category-badge monitoring__analysis-strategy-category-badge--page">
							{rec.target.content_type}
						</span>
					)}
				</div>
				{rec.summary && (
					<ExpandableText
						text={rec.summary}
						className="monitoring__analysis-strategy-rec-row-create-summary mb-0"
					/>
				)}
				{hasBrief && (
					<button
						type="button"
						onClick={onToggleBrief}
						className="monitoring__analysis-strategy-show-more btn btn-link btn-sm p-0"
					>
						View Brief {briefOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
					</button>
				)}
			</div>

			<div className="monitoring__analysis-strategy-rec-row-create-providers">
				<span className="monitoring__analysis-strategy-rec-col-label">Target Providers</span>
				<div className="monitoring__analysis-strategy-impact-icons">
					{PROVIDER_ORDER.map((p) => (
						<ProviderIcon
							key={p}
							provider={p}
							size={16}
							className={
								rec.provider_impact?.[toPayloadProvider(p)]
									? ""
									: "monitoring__analysis-strategy-impact-dim"
							}
						/>
					))}
				</div>
			</div>

			<div className="monitoring__analysis-strategy-rec-row-create-coverage">
				<span className="monitoring__analysis-strategy-rec-col-label">Coverage</span>
				<span
					className={`monitoring__analysis-strategy-coverage-stat ${
						TIER_TEXT_CLASS[tier] ?? TIER_TEXT_CLASS[0]
					}`}
				>
					{coverage}/3
				</span>
			</div>

			<div className="monitoring__analysis-strategy-rec-row-create-action">
				<span className="monitoring__analysis-strategy-rec-col-label">Suggested URL</span>
				{rec.target.suggested_url ? (
					<span className="monitoring__analysis-strategy-rec-row-suggested-url">
						{rec.target.suggested_url}
					</span>
				) : (
					<span className="small text-muted">—</span>
				)}
				<ButtonForWriting
					writingRunId={writingRunId}
					onUseWritingAssistant={onUseWritingAssistant}
					onViewContent={onViewContent}
				/>
			</div>

			{briefOpen && hasBrief && (
				<div className="monitoring__analysis-strategy-rec-row-detail">
					<ContentBriefPanel
						rec={rec}
						crossProviderSignals={crossProviderSignals}
					/>
				</div>
			)}
		</div>
	);
}

function ButtonForWriting({
	writingRunId,
	onUseWritingAssistant,
	onViewContent,
}) {
	if (writingRunId != null) {
		return (
			<button
				type="button"
				className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1 mt-1"
				onClick={() => onViewContent?.(writingRunId)}
			>
				<PenLine size={13} aria-hidden /> View Content
			</button>
		);
	}
	return (
		<button
			type="button"
			className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1 mt-1"
			onClick={onUseWritingAssistant}
		>
			<Sparkles size={13} aria-hidden /> Use AI Writing Assistant
		</button>
	);
}
